import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import UpgradePlanButton from '@features/Paywall/UpgradePlanButton';
import ContactSalesButton from '@features/Paywall/ContactSalesButton';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { useTranslation } from '@hooks';
import { BILLING_TYPE_MAP } from '@constants/billing';
import usePermissions from '@hooks/usePermissions';
import { RoutePath } from '@constants/routes';
import autoRepliesGb from 'assets/images/paywall/AutoReplies-GB.webp';
import autoRepliesEs from 'assets/images/paywall/AutoReplies-ES.webp';
import autoRepliesPt from 'assets/images/paywall/AutoReplies-PT.webp';
import autoRepliesUa from 'assets/images/paywall/AutoReplies-UA.webp';
import campaignsGb from 'assets/images/paywall/Campaigns-GB.webp';
import campaignsEs from 'assets/images/paywall/Campaigns-ES.webp';
import campaignsPt from 'assets/images/paywall/Campaigns-PT.webp';
import campaignsUa from 'assets/images/paywall/Campaigns-UA.webp';
import surveysGb from 'assets/images/paywall/Surveys-GB.webp';
import surveysEs from 'assets/images/paywall/Surveys-ES.webp';
import surveysPt from 'assets/images/paywall/Surveys-PT.webp';
import surveysUa from 'assets/images/paywall/Surveys-UA.webp';
import couponsGb from 'assets/images/paywall/Coupons-GB.webp';
import couponsEs from 'assets/images/paywall/Coupons-ES.webp';
import couponsPt from 'assets/images/paywall/Coupons-PT.webp';
import couponsUa from 'assets/images/paywall/Coupons-UA.webp';

const Container = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 12px) calc(50% - 12px)',
    gap: '24px',
    height: 'calc(var(--100vh) - 48px - var(--page-title-height))',
    '@media (max-width: 1024px)': {
        height: 'auto',
        gridTemplateColumns: '1fr',
    },
});

const ContentText = styled(Typography)({
    color: 'var(--text-secondary)',
    letterSpacing: '0 !important',
});

const ImageWrapper = styled('div')({
    display: 'flex',
    justifyContent: 'center',
});

const Image = styled('img')({
    width: '100%',
    maxWidth: '400px',
    height: '100%',
    objectFit: 'contain',
});

const ButtonContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    '@media (max-width: 600px)': {
        justifyContent: 'center',
        '& button': {
            width: '100%',
        },
    },
});

const PaywallType = {
    [RoutePath.Coupons]: 'Coupons',
    [RoutePath.Campaigns]: 'Campaigns',
    [RoutePath.Surveys]: 'Surveys',
    [RoutePath.ReviewsAutomation]: 'AutoReplies',
};

const ImageMap = {
    [`AutoReplies-GB`]: autoRepliesGb,
    [`AutoReplies-ES`]: autoRepliesEs,
    [`AutoReplies-PT`]: autoRepliesPt,
    [`AutoReplies-UA`]: autoRepliesUa,
    [`Campaigns-GB`]: campaignsGb,
    [`Campaigns-ES`]: campaignsEs,
    [`Campaigns-PT`]: campaignsPt,
    [`Campaigns-UA`]: campaignsUa,
    [`Surveys-GB`]: surveysGb,
    [`Surveys-ES`]: surveysEs,
    [`Surveys-PT`]: surveysPt,
    [`Surveys-UA`]: surveysUa,
    [`Coupons-GB`]: couponsGb,
    [`Coupons-ES`]: couponsEs,
    [`Coupons-PT`]: couponsPt,
    [`Coupons-UA`]: couponsUa,
};

const Paywall = ({ defaultPlan, onTryForFreeClick }) => {
    const account = useSelector(state => state.account?.account);
    const subscription = account?.subscriptions[0];
    const isTrialPlan = subscription?.billingPlanName === BILLING_TYPE_MAP.TRIAL;

    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const language = i18n.language;
    const { hasFullSettings } = usePermissions();

    const type = Object.entries(PaywallType).find(([routePath]) => pathname.startsWith(routePath))?.[1] || null;

    return (
        <Container>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h4" sx={{ marginBottom: '24px', fontWeight: '500 !important' }}>
                    {t(`Paywall.title${type}`)}
                </Typography>
                <ContentText variant="body2" sx={{ marginBottom: '7px' }}>
                    {t(`Paywall.subtitle${type}`)}
                </ContentText>
                <ContentText variant="body2" sx={{ marginBottom: '7px', fontWeight: '600 !important' }}>
                    {t('Paywall.whatYouGet')}
                </ContentText>
                <ContentText variant="body2">• {t(`Paywall.proposal1${type}`)}</ContentText>
                <ContentText variant="body2">• {t(`Paywall.proposal2${type}`)}</ContentText>
                <ContentText variant="body2">• {t(`Paywall.proposal3${type}`)}</ContentText>
                <ContentText variant="body2">• {t(`Paywall.proposal4${type}`)}</ContentText>
                <ContentText variant="body2" sx={{ margin: '7px 0 24px' }}>
                    {t('Paywall.unlockThis')}
                </ContentText>
                <ButtonContainer>
                    {hasFullSettings() && !isTrialPlan ? <UpgradePlanButton defaultPlan={defaultPlan} /> : null}
                    {isTrialPlan ? (
                        <Button variant="contained" onClick={onTryForFreeClick}>
                            {t('buttons.tryForFree')}
                        </Button>
                    ) : null}
                    <ContactSalesButton variant="outlined" />
                </ButtonContainer>
            </div>
            <ImageWrapper>
                <Image src={ImageMap[`${type}-${language}`]} alt={type} />
            </ImageWrapper>
        </Container>
    );
};

export default Paywall;
